import { useQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  IconButton,
} from '@mui/material';
import compact from 'lodash/compact';
import concat from 'lodash/concat';
import flatten from 'lodash/flatten';
import flow from 'lodash/flow';
import isNil from 'lodash/isNil';
import values from 'lodash/values';

import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CartController } from '../cart-controller';
import { AppContext } from '../../context/app-context';
import { CartContext, CartItem } from '../../context/cart-context';
import { CustomButton } from '../../elements/button';
import { Image } from '../../elements/image';
import { Loader } from '../../elements/loader';
import { Text } from '../../elements/text';
import { CALCULATE_ITEM_SUB_TOTAL, GET_ITEM } from '../../graphql/query';
import {
  CategoryType,
  Item,
  ItemSubTotal,
  ItemSubTotalPricing,
} from '../../types';
import { CustomisationOptionGroup } from '../../types/customisation';
import { getPriceWithCurrency } from '../../utils/charges';
import { Customisation } from './customisation';
import { customTheme } from '../../theme';
import { InformationModal } from '../information-modal';
import { ReactComponent as RSALimitIcon } from '../../assets/icons/RSALimitSticker.svg';
import { MenuItemGroupItem } from '../../types/vendor-menu';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';

interface ViewItemProps {
  item: { itemID: string; storeID?: string; menuID: string };
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showOnlyCustomisations?: boolean;
  onCloseModal?: () => void;
}

export const ViewItem = ({
  item,
  setIsModalOpen,
  showOnlyCustomisations = false,
  onCloseModal,
}: ViewItemProps) => {
  const { selectedVendor, serviceMethod, patronAdmitRSALimit, stadium } =
    useContext(AppContext);
  const { addToCart, cartLoading } = useContext(CartContext);
  const [itemCount, setItemCount] = useState<number>(1);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const formMethods = useForm();
  const { t } = useTranslation(['patron_portal', 'common']);

  const { watch } = formMethods;
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlcoholInfoModal, setAlcoholInfoModal] = useState(false);
  const selectedOptionsByOptionGroups = watch();
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showOnlyCustomisations) return;
    const handleScroll = () => {
      if (imageRef.current && containerRef.current) {
        const imageRect = imageRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const isImageOutOfView = imageRect.bottom <= containerRect.top;
        setShowStickyHeader(isImageOutOfView);
      }
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [showOnlyCustomisations]);

  const selectedCustomisations = useMemo(() => {
    return flow(
      values,
      compact,
      concat,
      flatten,
    )(selectedOptionsByOptionGroups);
  }, [selectedOptionsByOptionGroups]);

  const { data = { getItem: {} }, loading: itemLoading } = useQuery(GET_ITEM, {
    variables: {
      id: item.itemID,
      venueID: stadium?.value,
    },
  });
  const { data: itemSubTotal, loading: totalsLoading } = useQuery(
    CALCULATE_ITEM_SUB_TOTAL,
    {
      variables: {
        itemSubTotalCalculatorInput: {
          item: {
            itemID: item.itemID,
            menuID: item.menuID,
            count: itemCount,
            customisations: selectedCustomisations,
            serviceMethod: serviceMethod?.value,
          },
          venueId: stadium?.value,
        },
      },
      skip: isNil(data?.getItem),
    },
  );

  const thisItem = data?.getItem as Item;
  const { customisations, markupPrice: itemPrice } = thisItem;
  const { pricing = {} } =
    (itemSubTotal?.calculateItemSubTotal as ItemSubTotal) || {};

  const { subTotal = 0 } = pricing as ItemSubTotalPricing;

  const handleAddToCartClick = () => {
    thisItem.category === CategoryType.ALCOHOL && !patronAdmitRSALimit?.value
      ? setAlcoholInfoModal(true)
      : continueAddToCart();
  };

  const continueAddToCart = async () => {
    if (checkMandatoryOptionNotSelected()) {
      enqueueSnackbar(
        t('patron_portal.item.msgToastSelectOptions', { ns: 'patron_portal' }),
        {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        },
      );
      setError(true);
      setHelperText(
        t('patron_portal.item.msgSelectFromBelowOptions', {
          ns: 'patron_portal',
        }),
      );
      setErrorMessage(
        t('patron_portal.item.msgSelectMandatoryOption', {
          ns: 'patron_portal',
        }),
      );
    } else {
      setError(false);
      setHelperText('');
      const cartItemID = uuidv4();
      const cartItem: CartItem = {
        id: thisItem._id,
        name: thisItem.name,
        menuID: item.menuID,
        storeID: item.storeID,
        count: itemCount,
        customisations: selectedCustomisations,
      };
      addToCart(cartItemID, cartItem, itemCount, () => {
        if (showOnlyCustomisations && onCloseModal) {
          onCloseModal();
        } else {
          setIsModalOpen(false);
        }
      });
      enqueueSnackbar(
        t('patron_portal.item.msgItemAddSuccess', { ns: 'patron_portal' }),
        {
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        },
      );
      setItemCount(1);
    }
  };

  const handleRSAInfoContinue = () => {
    patronAdmitRSALimit?.setValue(true);
    continueAddToCart();
    setAlcoholInfoModal(false);
  };
  const isOptionGroupSelected = (
    optionGroup: CustomisationOptionGroup,
    minimumSelections: number,
  ) => {
    let selectedCount = 0;
    optionGroup.options.forEach((option) => {
      if (selectedCustomisations.includes(option._id.toString())) {
        selectedCount++;
      }
    });
    return selectedCount >= minimumSelections;
  };

  const checkMandatoryOptionNotSelected = () => {
    if (!thisItem?.customisations) return false;

    return thisItem.customisations.some((og: CustomisationOptionGroup) => {
      const selectedCount = og.options.filter((option) =>
        selectedCustomisations.includes(option._id.toString()),
      ).length;
      if (og.type === 'mandatory') {
        const min = og.minimumSelections ?? 1;
        return selectedCount < min;
      } else if (og.type === 'singleSelect') {
        return selectedCount !== 1;
      }

      return false;
    });
  };

  const areAllMandatoryOptionsSelected = useMemo(() => {
    if (!thisItem?.customisations) return true;

    return thisItem.customisations.every(
      (optionGroup: CustomisationOptionGroup) => {
        const selectedCount = optionGroup.options.filter((option) =>
          selectedCustomisations.includes(option._id.toString()),
        ).length;
        if (optionGroup.type === 'mandatory') {
          const min = optionGroup.minimumSelections ?? 1;
          return selectedCount >= min;
        }
        if (optionGroup.type === 'singleSelect') {
          return selectedCount === 1;
        }

        return true;
      },
    );
  }, [thisItem, selectedCustomisations]);

  return (
    <Grid
      ref={containerRef}
      container
      sx={{
        backgroundColor: '#f7f7f7',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        overflow: 'auto',
        paddingBottom: '75px',
        minHeight: showOnlyCustomisations? '' : '100vh',
        maxHeight: showOnlyCustomisations? '' : '100vh',
      }}
    >
      {!showOnlyCustomisations && showStickyHeader && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            padding: '12px 16px',
            transform: showStickyHeader ? 'translateY(0)' : 'translateY(-100%)',
            transition: 'transform 0.3s ease-in-out',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: '100%',
            }}
          >
            <IconButton
              onClick={() => setIsModalOpen(false)}
              sx={{
                width: 40,
                height: 40,
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <CloseIcon sx={{ color: '#000', fontSize: 20 }} />
            </IconButton>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
                marginLeft: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography
                  variant="itemName"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {thisItem?.name}
                </Typography>
                <Typography>
                  {itemPrice === 0
                    ? t('common.label.selectOptions', { ns: 'common' })
                    : getPriceWithCurrency(itemPrice)}
                </Typography>
              </Box>
              {thisItem?.imageUrl && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={thisItem.imageUrl}
                    alt={thisItem.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <IconButton
        onClick={() => setIsModalOpen(false)}
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 10,
          width: 48,
          height: 48,
          border: '2px solid #e0e0e0',
          borderRadius: '12px',
          backgroundColor: 'white',
          opacity: showStickyHeader ? 0 : 1,
          display: showOnlyCustomisations ? 'none' : 'flex',
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: showStickyHeader ? 'none' : 'auto',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <CloseIcon sx={{ color: '#000' }} />
      </IconButton>
      {showAlcoholInfoModal && (
        <InformationModal
          open={showAlcoholInfoModal}
          body={t('patron_portal.notice.msgRSASetsLimit', {
            ns: 'patron_portal',
          })}
          buttonTitle={t('patron_portal.modal.btnUnderstandContinue', {
            ns: 'patron_portal',
          })}
          bodyIcon={<RSALimitIcon />}
          handleContinue={handleRSAInfoContinue}
        />
      )}
      {itemLoading && <Loader visible={true} />}
      <Grid
        item
        container
        xs={12}
        md={10}
        margin="auto"
        mt={!showOnlyCustomisations ? { md: 10 } : 0}
      >
        {!showOnlyCustomisations && (
          <>
            <Grid item xs={12} md={6}>
              <Box
                ref={imageRef}
                sx={{
                  height: { minHeight: { lg: '95vh' } },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    height: { lg: 600 },
                    width: { lg: 450 },
                  }}
                >
                  {thisItem?.imageUrl && (
                    <Image
                      style={{
                        height: '100%',
                        borderRadius: 2,
                      }}
                      src={thisItem?.imageUrl}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              mt={thisItem?.imageUrl ? 2 : 8}
              pl={2}
              pr={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'start',
                     
                    }}
                  >
                    <Typography variant="itemName" mt={thisItem?.imageUrl ? '' : 3}>{thisItem.name}</Typography>
                    <Typography variant="itemDescription">
                      {thisItem.description}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 0.5 }}>
                    {error && (
                      <Typography
                        variant="errorText"
                        sx={{
                          color: areAllMandatoryOptionsSelected
                            ? 'green'
                            : '#8B4513',
                        }}
                      >
                        {errorMessage}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    mt={3}
                    mb={2}
                    display="flex"
                    justifyContent={'space-between'}
                  >
                    <Box mt={1}>
                      <Typography variant="itemPrice">
                        {itemPrice === 0
                          ? t('common.label.selectOptions', { ns: 'common' })
                          : getPriceWithCurrency(itemPrice)}
                      </Typography>
                    </Box>
                    <CartController
                      count={itemCount}
                      onIncrement={() => setItemCount(itemCount + 1)}
                      onDecrement={() => {
                        if (itemCount > 1) {
                          setItemCount(itemCount - 1);
                        }
                      }}
                    />
                  </Box>
                  <Divider sx={{ borderColor: 'black' }} />
                </Box>
                {!showOnlyCustomisations &&
                  thisItem?.customisations?.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="selectOwnChoiceText">
                        {t('patron_portal.order.msgSelectYourOwnChoices', {
                          ns: 'patron_portal',
                        })}
                      </Typography>
                    </Box>
                  )}
              </Box>
            </Grid>
          </>
        )}
        <Grid
          item
          px={2}
          xs={12}
          sx={{
            maxHeight: showOnlyCustomisations ? '50vh' : 'auto',
            width: showOnlyCustomisations ? '70vh' : 'auto',
            overflowY: showOnlyCustomisations ? 'auto' : 'visible',
          }}
        >
          <FormProvider {...formMethods}>
            {customisations?.map((customisation) => {
              return (
                <>
                  <Customisation
                    customisation={customisation}
                    itemCount={itemCount}
                    error={error}
                    helperText={helperText}
                  />
                </>
              );
            })}
          </FormProvider>
          <Box
            mt={2}
            mb={1}
            sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}
          >
            <Typography variant="labelTotal">
              {t('common.text.total', { ns: 'common' })}
            </Typography>

            <Typography variant="subTotalPrice" maxWidth="60%">
              {totalsLoading ? (
                <CircularProgress sx={{ width: '100%' }} size={20} />
              ) : (
                getPriceWithCurrency(subTotal)
              )}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box
        style={{
          position: showOnlyCustomisations ? 'fixed' : 'absolute',
          bottom: showOnlyCustomisations ? 7 : 0,
          right: 0,
          left: 0,
          minWidth: '50%',
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          style={{
            width: '80%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CustomButton
            disabled={totalsLoading || cartLoading}
            title={t('patron_portal.item.labelAddToCart', {
              ns: 'patron_portal',
            })}
            onClick={handleAddToCartClick}
            loading={cartLoading}
            style={{
              boxShadow:
                totalsLoading || cartLoading
                  ? '0px 0px 0px 8px grey, 0px 0px 8px 12px #999'
                  : '0px 0px 0px 8px #2A2A2A, 0px 0px 8px 12px #999',
            }}
            loadingText={
              <CircularProgress
                sx={{ width: '100%', color: '#D2FF28' }}
                size={20}
              />
            }
          />
        </Box>
      </Box>
    </Grid>
  );
};
